import { useCallback, useEffect, useMemo, useState } from "react";
import "../style.css";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import SEO from "../components/SEO";
import SiteFooter from "../components/SiteFooter";

const KNOWN_LAPTOP_BRANDS = [
  "Dell",
  "HP",
  "Lenovo",
  "Asus",
  "Acer",
  "Apple",
  "MSI",
  "Samsung",
  "Toshiba",
  "Sony",
  "Razer",
  "Microsoft",
  "Huawei",
  "Avita",
  "Infinix",
];

const parsePrice = (p) => {
  if (p === undefined || p === null) return 0;
  const n = String(p).replace(/[^\d.]/g, "");
  return Number(n || 0);
};

const toTitleCase = (value) =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const getLaptopBrand = (laptop) => {
  const explicitBrand = toTitleCase(laptop?.brand);
  if (explicitBrand) return explicitBrand;

  const name = String(laptop?.name || "").trim();
  if (!name) return "Other";

  const matchedKnownBrand = KNOWN_LAPTOP_BRANDS.find((brand) =>
    new RegExp(`\\b${brand}\\b`, "i").test(name)
  );
  if (matchedKnownBrand) return matchedKnownBrand;

  const firstWord = name.replace(/[^a-zA-Z0-9\s-]/g, "").split(/\s+/)[0];
  return toTitleCase(firstWord) || "Other";
};

const getLaptopPrice = (laptop) =>
  parsePrice(laptop?.discountPrice || laptop?.price);

const normalizeImageValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : [trimmed];
      } catch {
        return [trimmed];
      }
    }

    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [trimmed];
  }

  if (typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const getProductImages = (laptop) => {
  const base = [
    ...normalizeImageValues(laptop?.images),
    ...normalizeImageValues(laptop?.gallery),
    laptop?.image,
    laptop?.image1,
    laptop?.image2,
    laptop?.image3,
  ];

  return [...new Set(base.map((img) => String(img || "").trim()).filter(Boolean))];
};

function Products() {
  const [laptops, setLaptops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");



  // Filters (ONLY)
  const [search, setSearch] = useState("");
  const [sortPrice, setSortPrice] = useState("new"); // new | low | high
  const [brandFilter, setBrandFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");

  const loadLaptops = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const snapshot = await getDocs(collection(db, "laptops"));
      const laptopsData = snapshot.docs.map((doc) => ({
        _id: doc.id,
        ...doc.data(),
      }));

      // newest first
      laptopsData.sort((a, b) => b._id.localeCompare(a._id));
      setLaptops(laptopsData);
    } catch (error) {
      console.error("Error loading laptops:", error);
      setLoadError("Unable to load laptops. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLaptops();
  }, [loadLaptops]);

  const getWhatsAppLink = (laptop) => {
    const displayPrice = laptop.discountPrice || laptop.price;
    const message = `Hello! I'm interested in this laptop:

Name: ${laptop.name}
Price: ₹${displayPrice}
Specs: ${laptop.specs}`;

    return `https://wa.me/918838619488?text=${encodeURIComponent(message)}`;
  };

  const getDiscountPercent = (laptop) => {
    const price = Number(laptop.price);
    const discount = Number(laptop.discountPrice);
    if (!discount || !price || discount >= price) return null;
    return Math.round((1 - discount / price) * 100);
  };

  const brandOptions = useMemo(() => {
    const brands = new Set(
      laptops.map((laptop) => getLaptopBrand(laptop)).filter(Boolean)
    );
    return Array.from(brands).sort((a, b) => a.localeCompare(b));
  }, [laptops]);

  const filteredLaptops = useMemo(() => {
    let arr = [...laptops];

    // Search
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((l) => {
        const name = String(l.name || "").toLowerCase();
        const specs = String(l.specs || "").toLowerCase();
        return name.includes(q) || specs.includes(q);
      });
    }

    // Brand filter
    if (brandFilter !== "all") {
      arr = arr.filter((laptop) => getLaptopBrand(laptop) === brandFilter);
    }

    // Price range filter
    if (priceFilter !== "all") {
      arr = arr.filter((laptop) => {
        const amount = getLaptopPrice(laptop);
        if (priceFilter === "under20000") return amount < 20000;
        if (priceFilter === "20000-30000") return amount >= 20000 && amount <= 30000;
        if (priceFilter === "30000-50000") return amount > 30000 && amount <= 50000;
        if (priceFilter === "above50000") return amount > 50000;
        return true;
      });
    }

    // Sort by price
    if (sortPrice === "low") {
      arr.sort((a, b) => getLaptopPrice(a) - getLaptopPrice(b));
    } else if (sortPrice === "high") {
      arr.sort((a, b) => getLaptopPrice(b) - getLaptopPrice(a));
    }

    return arr;
  }, [laptops, search, sortPrice, brandFilter, priceFilter]);

  const clearFilters = () => {
    setSearch("");
    setSortPrice("new");
    setBrandFilter("all");
    setPriceFilter("all");
  };

  return (
    <>


<SEO
  title="Used Laptops Collection"
  description="Browse all available used laptops in Namakkal. Dell, HP, Lenovo, i5, i7 laptops at best price with support."
  url="https://lappylaptops.com/products"
/>


      <div className="products">
        {loadError && (
          <div className="page-error-banner">
            <p>{loadError}</p>
            <button type="button" className="btn-primary" onClick={loadLaptops}>
              Retry
            </button>
          </div>
        )}

        {/* TOP BANNER */}
        <div className="products-top">
          <div className="top-left">
            <h2 className="top-title">All Laptops</h2>
            <p className="top-sub">Best deals • Warranty • Service support</p>
          </div>

          {!loading && (
            <span className="top-badge">{filteredLaptops.length} Results</span>
          )}
        </div>

        {/* FILTER BAR (ONLY SEARCH + PRICE) */}
        <div className="filters-bar simple">
          <input
            className="filter-input"
            placeholder="Search Dell, i5, 8GB, SSD..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="filter-select"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
          >
            <option value="all">Brand: All</option>
            {brandOptions.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={priceFilter}
            onChange={(e) => setPriceFilter(e.target.value)}
          >
            <option value="all">Price: All</option>
            <option value="under20000">Under Rs 20,000</option>
            <option value="20000-30000">Rs 20,000 - Rs 30,000</option>
            <option value="30000-50000">Rs 30,000 - Rs 50,000</option>
            <option value="above50000">Above Rs 50,000</option>
          </select>

          <select
            className="filter-select"
            value={sortPrice}
            onChange={(e) => setSortPrice(e.target.value)}
          >
            <option value="new">Sort: Newest</option>
            <option value="low">Price: Low → High</option>
            <option value="high">Price: High → Low</option>
          </select>

          <button className="filter-clear" onClick={clearFilters}>
            Clear
          </button>
        </div>

        {/* Grid */}
        <div className="grid">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div className="card skeleton-card" key={i}>
                <div className="sk-img"></div>
                <div className="sk-lines">
                  <div className="sk-line title"></div>
                  <div className="sk-line"></div>
                  <div className="sk-line short"></div>
                  <div className="sk-btn"></div>
                </div>
              </div>
            ))
          ) : filteredLaptops.length === 0 ? (
            <div className="empty-state">
              <h3>No laptops found</h3>
              <p>Try different keywords or clear filters.</p>
              <button className="btn-primary" onClick={clearFilters}>
                Reset
              </button>
            </div>
          ) : (
            filteredLaptops.map((l) => {
              const productImages = getProductImages(l);
              const mainImage = productImages[0] || "placeholder.png";
              const discountPercent = getDiscountPercent(l);
              return (
                <div key={l._id} className="card">
                  <Link to={`/products/${l._id}`} className="product-image-link">
                    <div className="product-image-wrap">
                      {discountPercent && (
                        <span className="discount-badge-top">
                          {discountPercent}% OFF
                        </span>
                      )}
                      <img
                        src={mainImage}
                        alt={l.name}
                        width="200"
                        onError={(e) => (e.currentTarget.src = "placeholder.png")}
                      />
                    </div>
                  </Link>

                  <Link to={`/products/${l._id}`} className="product-link">
                    <h3>{l.name}</h3>
                  </Link>
                  <div className="price-row">
                    {l.discountPrice ? (
                      <>
                        <span className="price-new">₹{l.discountPrice}</span>
                        <span className="price-old">₹{l.price}</span>
                      </>
                    ) : (
                      <span className="price-new">₹{l.price}</span>
                    )}
                  </div>

                  {(l.description || l.specs) && (
                    <p className="product-full-desc">
                      {l.description || l.specs}
                    </p>
                  )}

                  <button
                    className="btn-secondary whatsapp-btn"
                    onClick={() => window.open(getWhatsAppLink(l), "_blank")}
                  >
                    💬 Chat on WhatsApp
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* CONTACT SECTION */}
      <section className="contact">
        <div>
          <h2 className="get">Get in Touch</h2>
          <p>📞 +91 8838619488</p>
          <p>✉️ <a href="mailto:lappylaptopsales@gmail.com" className="contact-email-link">lappylaptopsales@gmail.com</a></p>
          <p>📍 129 Salapalayam Road, Reddipatti, Namakkal, TamilNadu 637002</p>

          <p>📍 Lappy Laptops, 27, Devangar Street, Thathiengarpettai, Musiri-Tk, Trichy-Dt, Pincode: 621214. Landmark: Mini Radios.</p>
          <a
            href="https://wa.me/918838619488"
            target="_blank"
            rel="noreferrer"
            className="whatsapp"
          >
            WhatsApp Us
          </a>
        </div>

        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3913.730200578514!2d78.22037987429323!3d11.207586751094425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3babc94be384bbb3%3A0xcdd0430efbea64f3!2sLappy%20Used%20Laptops%20Sales!5e0!3m2!1sen!2sin!4v1770279618891!5m2!1sen!2sin"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            title="Lappy Location"
          ></iframe>
        </div>
      </section>
      <SiteFooter />
    </>
  );
}

export default Products;


